import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { HttpClient } from '@angular/common/http';
import { FilterConfig, GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { debounceTime, finalize, Subject } from 'rxjs';
import { ACKNOWLEDGEABLE_ERROR_STATUS_CODES, ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES, MAX_INT_32 } from '../../shared/constants/values';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, SortDirection } from '@angular/material/sort';
import { GridDataService } from '../../shared/dynamic-grid/grid-service/grid-data.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import moment from 'moment';
import { BREADCRUMB_NAMES } from '../../shared/constants/breadCrumbPaths';
import { marked } from 'marked';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { ColumnPreferencesService } from '../../shared/column-preference/columnpreference.service';

const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs;
(pdfMake as any).vfs = vfs;

export interface ColumnPreference {
  PreferenceKeyName: string;
  HiddenColumns: Set<string>;
}

// Helper interfaces and functions for PDF export
interface ExportPdfOptions {
  title?: string;
  watermarkText?: string;
  colorRules?: any[];
  forceSingleTable?: boolean;
  pageSize?: string;
  pageOrientation?: string;
  baseFontSize?: number;
  minFontSize?: number;
  targetPadding?: number;
  lineHeight?: number;
  fieldsToTrim?: Set<string>;
  data?: any[];
  exportFileName?: string;
}

interface ColumnConfig {
  label: string;
  jsonKey: string;
  dataType?: string;
}

// Helper functions
function formatCellByType(column: ColumnConfig, value: any, locale: string = 'en-US'): string {
  if (!value || value === '' || value === null || value === undefined) {
    return 'LOAD DID NOT RUN';
  }

  if (column.dataType === 'date') {
    return new Date(value).toLocaleDateString(locale);
  }

  if (column.dataType === 'number') {
    return new Intl.NumberFormat(locale).format(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return String(value);
}

function computeFitSettings(headers: string[], fields: string[], data: any[], pageSize: string, orientation: string, margins: number[], baseFontSize: number, minFontSize: number, targetPadding: number) {
  // Simple implementation - return base settings
  return { fontSize: baseFontSize, pageSize };
}

function timestamp(): string {
  return new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
}

@Component({
  selector: 'app-last-seven-days-load',
  templateUrl: './last-seven-days-load.component.html',
  styleUrl: './last-seven-days-load.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LastSevenDaysLoadComponent implements OnInit {
  onCheckboxChange($event: Event, arg1: string) {
    throw new Error('Method not implemented.');
  }
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;
  @Input() gridConfig!: GridConfig;
  @Input() getDataURL!: string;
  @Input() getPMSURL!: string;
  @Input() postUrl!: string;
  @Input() deleteUrl!: string;
  @Input() toggleUrl!: string;
  @Input() customIsErrorLog: (element: any) => boolean;
  @Input() onAdd!: Function;
  @Input() onEdit!: Function;
  @Input() sortBy: string = '';
  @Output() addClicked = new EventEmitter<boolean>();
  @Output() editClicked = new EventEmitter<any>();
  @Output() onRowClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() onToggleShowToastEmitter: EventEmitter<Toast> = new EventEmitter<Toast>();

  dataSource = new MatTableDataSource();
  private searchSubject = new Subject<string>();
  minFromDate: string = '';
  maxToDate: string = '';
  displayedColumns!: string[];
  isDropdownOpen: boolean = false;
  showDateFilterDropdown = false;
  hasFilters: boolean;
  isExportMenuOpen: boolean;
  isLoading: boolean = true;
  pageNumber: number = 1;
  pageSize: number = 20;
  searchInputValue: string = '';
  searchQuery: string = '';
  showErrorCard: boolean = false;
  errorTitle: string = '';
  errorMessage: string = '';
  @Input() sortOrder: string = 'asc';
  totalItems: number;
  isServerSidePagination: boolean = true;
  pageChanged: boolean = true;
  pageSizeOptions: number[] = [20, 50, 100, MAX_INT_32];
  refreshTimeout: any;
  filters: { [key: string]: any } = {};
  alwaysSelectedCheckboxOptions: { [jsonKey: string]: any[] } = {};

  isFilterDropdownOpen: { [key: string]: boolean } = {};
  customDateRange = {
    from: '',
    to: '',
  };

  isDateInputDisabled: boolean = false;
  userRole = localStorage.getItem('userRole');
  userEmail = localStorage.getItem('userEmail');
  filtered: { [key: string]: any } = {};
  appliedFilter: boolean = false;
  resetAll: boolean = false;
  uniqueTableName: string;
  hideAuditColumns: boolean = JSON.parse(localStorage.getItem('hideAuditColumns')) || false;
  emptyDataMessage: string = 'No data found at this moment';
  private _totalRecordsCount: number = 0;

  // Status color mapping
  statusLegend = [
    { label: 'Completed', color: '#c6f6c6', textColor: '#0a4d0a', count: 0 },
    { label: 'In Progress', color: '#fff3cd', textColor: '#856404', count: 0 },
    { label: 'Error/Failed', color: '#f8c6c6', textColor: '#6b0000', count: 0 },
    { label: 'Load Didn\'t Run', color: '#f8c6c6', textColor: '#6b0000', count: 0 },
    { label: 'Not Installed', color: '#d1d5db', textColor: '#4b5563', count: 0 },
    { label: 'Disabled', color: '#e0e0e0', textColor: '#666666', count: 0 }
  ];
  totalCount = 0;
  selectedColumn = '';
  selectedColumnKey = '';
  isFiltered = false;
  originalData: any[] = [];

  isLiveFilterActive = false;
  isColumnSelectorOpen = false;
  isSavingPreferences = false;
  visibleColumns: Set<string> = new Set();
  private userId: string | null = null;
  columnPreference: ColumnPreference;

  constructor(
    private http: HttpClient,
    private gridDataService: GridDataService,
    public dialog: MatDialog,
    private _router: Router,
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private datePipe: DatePipe,
    private columnPrefService: ColumnPreferencesService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth
  ) { }

  getStatusColor(status: string, isDisabled?: boolean, isInstalled?: boolean): { backgroundColor: string, color: string } {
    // If practice is disabled, always return gray regardless of status
    if (isDisabled) {
      return { backgroundColor: '#e0e0e0', color: '#666666' };
    }

    // If practice is not installed, always return not installed color regardless of status
    if (isInstalled === false) {
      return { backgroundColor: '#d1d5db', color: '#4b5563' };
    }

    if (!status || status === '' || status === null || status === undefined || status.toString().trim() === '' || status.toString().trim() === '-') {
      return { backgroundColor: '#f8c6c6', color: '#6b0000' };
    }

    const statusLower = status.toLowerCase();

    if (statusLower.includes('completed') || statusLower.includes('success')) {
      return { backgroundColor: '#c6f6c6', color: '#000000' };
    } else if (statusLower.includes('failed') || statusLower.includes('error') || statusLower.includes('unhealthy') || statusLower.includes('interrupted') || status.toString().trim() === 'LOAD DID NOT RUN') {
      return { backgroundColor: '#f8c6c6', color: '#6b0000' };
    }
    else if (statusLower.includes('progress') || statusLower.includes('healthy') || statusLower.includes('batch')) {
      return { backgroundColor: '#fff3cd', color: '#856404' };
    }

    return { backgroundColor: '', color: '' };
  }

  getGridColumnNames() {
    return this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map((e) => e.jsonKey);
  }

  async ngOnInit(): Promise<void> {
    this.gridConfig = {
      columns: null,
      filters: [
        { label: 'PMS', jsonKey: 'pmsIds', options: [], type: 'multi' }
      ],
      editable: false,
      deletable: false,
      exportable: true,
      uniqueTableName: 'LastSevenDaysLoadReport'
    };

    // Set default filters
    this.filters = {
      pmsIds: [],
      sortOrder: 'asc',
      isLive: null
    };

    this.getDataURL = API_ENDPOINTS.LAST_SEVEN_DAYS_LOAD;
    this.getPMSURL = API_ENDPOINTS.PMS_MASTER;
    this.hasFilters = this.gridConfig.filters == undefined ? false : true;
    this.uniqueTableName = this.gridConfig.uniqueTableName;
    this.isLoading = true;
    setTimeout(() => {
      this.columnPreference = this.getPreferenceKeyName();
      this.loadColumnPreferences(this.columnPreference.PreferenceKeyName);
    }, 0);
    await this.fetchData();
    this.isLoading = true;
    await this.fetchPMSOptions();

    this.searchSubject.pipe(debounceTime(500)).subscribe((searchQuery) => {
      this.searchQuery = searchQuery;
      this.pageNumber = 1;
      this.fetchData();
    });
  }

  ngAfterViewInit() {
    this.sortBy = 'startTime';
    if (this.sort) {
      this.sort.active = this.sortBy;
      this.sort.direction = this.sortOrder as SortDirection;
      this.sort.start = this.sortOrder as SortDirection;
      this.dataSource.sort = this.sort;
      this.sort.sortChange.emit();
      this.sort.sortChange.subscribe(() => {
        this.sortBy = this.sort.active;
        this.sortOrder = this.sort.direction;
        this.pageNumber = 1;
        this.fetchData();
      });
    }
    this.changeDetectorRef.detectChanges();
  }

  shouldHideButton(filterConfig: FilterConfig): boolean {
    return filterConfig.type === 'date' && !this.showDateFilterDropdown;
  }

  extractDateFromISOString(dateTimeStr: string): string {
    if (!dateTimeStr) return '';
    return new Date(dateTimeStr).toISOString().split('T')[0];
  }

  isFilterOptionSelected(filterConfig: FilterConfig, option: any): boolean {
    return this.filters[filterConfig.jsonKey]?.some(
      (selectedOption: any) => selectedOption.id === option.id
    ) || false;
  }

  isAlwaysSelectedCheckboxOption(jsonKey: string, optionId: string): boolean {
    return false;
  }

  getSelectedOptionNames(filterConfig: FilterConfig): string {
    if (this.isAllOptionsSelected(filterConfig)) {
      return 'All';
    }

    const selectedOptions = filterConfig.options?.data?.filter((option: any) =>
      this.filters[filterConfig.jsonKey]?.some(
        (selectedOption: any) => selectedOption.id === option.id
      )
    );
    const selectedOptionNames = selectedOptions?.map((option: any) => option.name) || [];

    return selectedOptionNames.length > 1
      ? `${selectedOptionNames[0]}...`
      : selectedOptionNames[0] || '';
  }

  refreshHandler() { }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  onFilterOptionClick(filter: FilterConfig, option: any): void {
    if (filter.type === 'single') {
      this.filters[filter.jsonKey] = option.id;
    } else if (filter.type === 'multi') {
      if (!this.filters[filter.jsonKey]) {
        this.filters[filter.jsonKey] = [];
      }
      const index = this.filters[filter.jsonKey].indexOf(option);
      if (index === -1) {
        this.filters[filter.jsonKey].push(option);
      } else {
        this.filters[filter.jsonKey].splice(index, 1);
      }
    }
    this.pageNumber = 1;
  }

  onFilterOptionChange(
    filterConfig: FilterConfig,
    option: any,
    checked: boolean
  ): void {
    const jsonKey = filterConfig.jsonKey;

    if (!this.filters[jsonKey]) {
      this.filters[jsonKey] = [];
    }

    if (option.id === 'All') {
      if (checked) {
        this.filters[jsonKey] = [...filterConfig.options.data];
      } else {
        this.filters[jsonKey] = [];
      }
      return;
    }

    if (checked) {
      // Add if not already present
      const exists = this.filters[jsonKey].some((item: any) => item.id === option.id);
      if (!exists) {
        this.filters[jsonKey].push(option);
      }
    } else {
      // Remove the option
      this.filters[jsonKey] = this.filters[jsonKey].filter((item: any) => item.id !== option.id);
      // Remove 'All' option when any individual option is deselected
      this.filters[jsonKey] = this.filters[jsonKey].filter((item: any) => item.id !== 'All');
    }

    if (this.filters[jsonKey].length === 0) {
      delete this.filters[jsonKey];
    }
  }

  onCustomDateChange(filterConfig: FilterConfig): void {
    const from = new Date(this.customDateRange.from);
    const to = new Date(this.customDateRange.to);

    // Optional: prevent future dates or invalid ranges
    if (from && to && from <= to) {
      this.filters[filterConfig.jsonKey] = {
        startDate: from.toISOString(),
        endDate: to.toISOString(),
      };
    }
  }

  private configureMarked(): void {
    const renderer = new marked.Renderer();
    renderer.link = ({ href, title, text }) => {
      const titleAttr = title ? ` title="${title}"` : '';
      return `<a href="${href}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
    };
    marked.setOptions({ renderer });
  }

  // Displayed columns based on grid config
  setupColumns(): void {
    this.displayedColumns =
      this.gridConfig.editable || this.gridConfig.deletable
        ? ['action', ...this.gridConfig.columns.map((c) => c.jsonKey)]
        : this.gridConfig.columns.map((c) => c.jsonKey);

    this.visibleColumns = new Set(this.displayedColumns);
  }

  disableSuccessAcknowldgeToggle(element: any): boolean {
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_SUCCESS_STATUS_CODES.includes(
        element.pmsStatusCode
      )
        ? true
        : false;
    }
    return false;
  }

  onToggleChange(element: any, jsonKey: string, toggleValue: boolean): void {
    const toggleUrl = this.gridConfig.columns.find(
      (column) => column.jsonKey === jsonKey
    )?.toggleUrl;

    const patchUrl = toggleUrl
      ? toggleUrl.replace(':id', element.id.toString())
      : this.toggleUrl;

    this.gridDataService
      .toggleData(patchUrl, element.id, jsonKey, toggleValue)
      .subscribe(
        (response) => {
          element[jsonKey] = response[jsonKey];
          const index = this.dataSource.data.findIndex(
            (item: any) => item.id === element.id
          );
          if (index !== -1) {
            this.dataSource.data[index][jsonKey] = response[jsonKey];
          }
          this.onToggleShowToastEmitter.emit({
            severity: 'success',
            title: 'Success',
            message: response?.data,
          });
        },
        (error) => {
          console.error('Error updating toggle value:', error);
          this.onToggleShowToastEmitter.emit({
            severity: 'error',
            title: 'Error',
            message: error?.error?.errorMessage,
          });
        }
      );
  }

  formatDateTime(date: string | Date) {
    if (date == null || date == '')
      return '';
    const userTimeZone = moment.tz.guess();
    const userDateTime = moment.utc(date).tz(userTimeZone);
    return userDateTime.format('YYYY-MM-DD HH:mm');
  }

  getMakrDownText(isMarkDownSupported: boolean, text: string) {
    return isMarkDownSupported ? marked(text) : '';
  }

  formatNumber(value: number | null): string | number {
    return value ? new Intl.NumberFormat('en-US').format(value) : value;
  }

  rowClick(data, event) {
    this.onRowClick.emit(data);
  }

  // Make an API call to fetch grid data
  toggleIsLiveFilter(): void {
    this.isLiveFilterActive = !this.isLiveFilterActive;
    this.filters['isLive'] = this.isLiveFilterActive ? true : null;
    this.pageNumber = 1;
    this.fetchData();
  }

  fetchData(): void {
    this.isLoading = true;

    const localFilters = JSON.parse(JSON.stringify(this.filters));
    localFilters.sortOrder = this.sortOrder;

    if (localFilters['pmsIds']) {
      localFilters['pmsIds'] = localFilters['pmsIds'].filter(
        (opt: any) => opt.id !== 'All'
      );
    }

    if (localFilters['isLive'] === null) {
      delete localFilters['isLive'];
    }

    this.gridDataService
      .fetchData(
        this.getDataURL,
        this.pageNumber,
        this.pageSize,
        this.searchQuery,
        this.sortBy,
        this.sortOrder,
        localFilters
      )
      .subscribe({
        next: (response: any) => {
          if (!response.isSuccess) {
            this.handleError({
              status: response.statusCode,
              error: {
                errorMessage: response.errorMessage,
                detailedErrorMessage: response.detailedErrorMessage,
              },
            });
            return;
          }

          // Columns first - add pmsName as first column
          const apiColumns = response.data.gridDisplayColumns;

          this.gridConfig.columns = [
            {
              label: 'Live',
              jsonKey: 'isLive',
              dataType: 'toggle',
              isSortable: false
            },
            {
              label: 'PMS Name',
              jsonKey: 'pmsName',
              dataType: 'string',
              isSortable: false
            },
            ...apiColumns.map(col => ({
              label: col,
              jsonKey: this.toSafeJsonKey(col),
              dataType: 'string',
              isSortable: false
            }))
          ];

          this.setupColumns(); // Displayed columns on UI

          // Data
          let rawData = response.data.lastSevenDaysLoadData;

          rawData = rawData.map(item => {
            const flat: any = { ...item };

            for (let dateKey of Object.keys(item.dateColumns)) {
              const safeKey = dateKey.replace(/-/g, "_");
              flat[safeKey] = item.dateColumns[dateKey];
            }

            delete flat.dateColumns; // optional
            return flat;
          });
          this.dataSource.data = rawData || [];
          this.originalData = [...(rawData || [])];
          this.totalItems = response.totalRecordsCount;
          this.emptyDataMessage =
            (!rawData || rawData.length === 0)
              ? "There are no items to display at the moment."
              : "";

          this.calculateStatusCounts();
          this.isLoading = false;
          this.getDateFilterLabel();
          this.changeDetectorRef.markForCheck();
        },

        error: (err) => {
          console.error("API Error:", err);
          this.handleError(err);
          this.isLoading = false;
        }
      });
  }

  toSafeJsonKey(label: string): string {
    if (label === "Practice Code")
      return "practiceCode";

    return label
      .replace(/\s+/g, '_')
      .replace(/-/g, '_')
      .toLowerCase();
  }

  // Make an API call to fetch PMS options
  async fetchPMSOptions() {
    this.isLoading = true;
    this.http.get<any>(this.getPMSURL, { params: { pageSize: MAX_INT_32 } }).subscribe(response => {
      let PMSList = response.data.map(option => ({
        ...option,
        name: `${option.name} ${option.version}`,
        id: option.id,
        checked: true
      })).sort((a, b) => a.name.localeCompare(b.name));

      PMSList.unshift({
        name: 'All',
        id: 'All',
        checked: true
      });

      response.data = PMSList;

      this.gridConfig.filters.find(filter => filter.jsonKey === 'pmsIds').options = response;

      this.filters["pmsIds"] = PMSList.slice();

      let ff = this.gridConfig.filters.find(filter => filter.jsonKey === 'pmsIds');
      this.getSelectedOptionNames(ff);

      this.changeDetectorRef.detectChanges();
    }, error => {
      console.error('Error fetching PMS options:', error);
      this.isLoading = false;
    });
  }

  toggleFilterDropdown(filterKey: string, event: Event): void {
    for (let key in this.isFilterDropdownOpen) {
      if (filterKey !== key) {
        this.isFilterDropdownOpen[key] = false;
      }
    }
    this.isFilterDropdownOpen[filterKey] =
      !this.isFilterDropdownOpen[filterKey];
  }

  isFilterApplied(filterConfig: FilterConfig): boolean {
    return (
      this.filters[filterConfig.jsonKey] &&
      this.filters[filterConfig.jsonKey].length > 0
    );
  }

  getDateFilterLabel(): string {
    if (!this.customDateRange.from || !this.customDateRange.to) {
      return '';
    }

    const from = new Date(this.customDateRange.from);
    const to = new Date(this.customDateRange.to);

    const diffDays = Math.ceil(
      (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
    ) + 1;

    return `Last ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  }

  hasFilterOptionsSelected(filterConfig: FilterConfig): boolean {
    if (filterConfig.type === 'single') {
      return !!this.filters[filterConfig.jsonKey];
    } else if (filterConfig.type === 'multi') {
      return !!this.filters[filterConfig.jsonKey]?.length;
    } else if (filterConfig.type === 'date') {
      return !!this.filters[filterConfig.jsonKey];
    }
    return false;
  }

  getAppliedFilterCount(filterConfig: FilterConfig): number {
    if (filterConfig.type === 'single') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    } else if (filterConfig.type === 'multi') {
      return this.filters[filterConfig.jsonKey]?.length || 0;
    } else if (filterConfig.type === 'date') {
      return this.filters[filterConfig.jsonKey] ? 1 : 0;
    }
    return 0;
  }

  resetFilter() {
    // Reset to initial state with all PMS options selected
    const pmsFilter = this.gridConfig.filters.find(filter => filter.jsonKey === 'pmsIds');
    if (pmsFilter && pmsFilter.options && pmsFilter.options.data) {
      this.filters = {
        pmsIds: [...pmsFilter.options.data],
        sortOrder: 'asc',
        isLive: null
      };
    } else {
      this.filters = {
        pmsIds: [],
        sortOrder: 'asc',
        isLive: null
      };
    }
    this.isLiveFilterActive = false;

    this.customDateRange = {
      from: '',
      to: ''
    };

    this.appliedFilter = false;
    this.resetAll = true;
    this.searchQuery = '';
    this.searchInputValue = '';
    localStorage.removeItem('choosenFilters');
    this.fetchData();
    this.closeFilterDropdowns();
  }

  // Export data to Excel
  exportData(): void {
    this.isLoading = true;
    this.changeDetectorRef.detectChanges();

    const data = this.dataSource.data;

    if (data === undefined || data === null || data.length === 0) {
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.toastComponent?.showToast({
        severity: 'warn',
        title: 'Warning',
        message: 'No data available to export.',
        duration: 3000
      });
      this.changeDetectorRef.detectChanges();
      return;
    }

    // Clone columns config
    const columns = this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(column => ({ ...column }));
    const headers = columns.map(column => column.label.toUpperCase());

    // Convert data to JSON with transformations
    const jsonData = data.map((row: any) => {
      const rowData: any = {};
      columns.forEach((column) => {
        const value = row[column.jsonKey];

        // Check for blank/null/empty values first
        if (!value || value === '' || value === null || value === undefined || value.toString().trim() === '' || value.toString().trim() === '-') {
          rowData[column.label.toUpperCase()] = 'LOAD DID NOT RUN';
        } else if (typeof value === 'boolean') {
          rowData[column.label.toUpperCase()] = value ? 'Yes' : 'No';
        } else if (column.dataType === 'date') {
          const formattedDate = moment
            .utc(value, 'YYYY-MM-DD HH:mm:ss')
            .local()
            .format('YYYY-MM-DD HH:mm:ss');
          rowData[column.label.toUpperCase()] = formattedDate;
        } else {
          rowData[column.label.toUpperCase()] = value;
        }
      });

      return rowData;
    });

    // Create workbook + worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Add header row
    const headerRow = worksheet.addRow(headers);

    headerRow.eachCell(cell => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Add data rows with color coding
    jsonData.forEach((row, index) => {
      if (row.statusCode === 200) {
        row['STATUS'] = 'Success';
      }
      const addedRow = worksheet.addRow(Object.values(row));
      const originalRowData = data[index];

      // Apply color coding to status cells
      addedRow.eachCell((cell, colNumber) => {
        const columnKey = columns[colNumber - 1]?.jsonKey;
        const cellValue = row[headers[colNumber - 1]];

        // Check if this row is disabled and apply gray background to entire row
        if ((originalRowData as any)?.isDisabled) {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' } // Gray background for disabled practices
          };
          cell.font = { color: { argb: 'FF666666' }, bold: true };
        } else if (cellValue && typeof cellValue === 'string') {
          const colors = this.getStatusColor(cellValue, (originalRowData as any)?.isDisabled, (originalRowData as any)?.isInstalled);
          if (colors.backgroundColor) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: colors.backgroundColor.replace('#', 'FF') }
            };
            cell.font = { color: { argb: colors.color.replace('#', 'FF') }, bold: true };
          }
        }
      });
    });

    // Column widths
    worksheet.columns = columns.map(() => ({ width: 20 }));

    // Generate filename
    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];

    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath],
      'xlsx'
    );

    // Write the Excel file
    workbook.xlsx.writeBuffer().then(buffer => {
      saveAs(
        new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }),
        fileName
      );

      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }).catch(error => {
      console.error('Error exporting data:', error);
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    });

    this.isDropdownOpen = false;
  }

  // Export data to PDF
  async exportDataToPDF(reportTitle: string, reportType: string): Promise<void> {
    let data: any[] = [];
    switch (reportType) {
      case 'All':
        data = this.dataSource.data;
        break;
      default:
        data = this.dataSource.data;
        break;
    }

    if (data === undefined || data === null || data.length === 0) {
      this.isColumnSelectorOpen = false;
      this.isSavingPreferences = false;
      this.isDropdownOpen = false;
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.toastComponent?.showToast({
        severity: 'warn',
        title: 'Warning',
        message: 'No data available to export.',
        duration: 3000
      });
      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.markForCheck();
      return;
    }

    // Prepare data: format boolean and date fields
    data.forEach((row: any) => {
      this.gridConfig.columns.forEach((column) => {
        if (!this.visibleColumns.has(column.jsonKey)) {
          return; // Skip hidden columns
        }
        const value = row[column.jsonKey];

        // Check for blank/null/empty values first
        if (!value || value === '' || value === null || value === undefined || value.toString().trim() === '' || value.toString().trim() === '-') {
          row[column.jsonKey] = 'LOAD DID NOT RUN';
        } else if (typeof value === 'boolean') {
          row[column.jsonKey] = value ? 'Yes' : 'No';
        } else if (column.dataType === 'date') {
          const formattedDate = moment
            .utc(value, 'YYYY-MM-DD HH:mm:ss')
            .local()
            .format('YYYY-MM-DD HH:mm:ss');
          row[column.jsonKey] = formattedDate;
        } else {
          row[column.jsonKey] = value;
        }
      });
    });

    const currentURLSegements = this._router.url.split('/');
    const currentPath = currentURLSegements[currentURLSegements.length - 1];
    const fileName = this.generateFilename(
      BREADCRUMB_NAMES[currentPath] || 'Export',
      'pdf'
    );

    this.isLoading = false;
    this.isDropdownOpen = false;
    this.changeDetectorRef.detectChanges();

    // Call the PDF export function with prepared data
    this.exportCurrentDataToPDF({
      title: `${BREADCRUMB_NAMES[currentPath].replace(' ', '_')} - ${reportType}`,
      watermarkText: 'Dentalcorp',
      colorRules: [],
      forceSingleTable: true,
      pageSize: 'A3',
      pageOrientation: 'landscape',
      baseFontSize: 7,
      minFontSize: 6,
      targetPadding: 1,
      lineHeight: 0.9,
      fieldsToTrim: new Set([]),
      data: data,
      exportFileName: fileName
    });
  }

  // Export current data to PDF
  exportCurrentDataToPDF(options: ExportPdfOptions): void {
    const data: any[] = options.data || [];
    const colConfigs: ColumnConfig[] = this.gridConfig.columns
      .filter(col => this.visibleColumns.has(col.jsonKey))
      .map(c => ({ ...c }));

    // Build pdf columns from ColumnConfig
    const pdfColumns = colConfigs.map(c => ({
      header: c.label,
      field: c.jsonKey
    }));

    const title = options?.title ?? 'Export — Data';
    const watermarkText = options?.watermarkText ?? 'Dentalcorp';
    const pageSize = options?.pageSize ?? 'A4';
    const orientation = options?.pageOrientation ?? 'landscape';
    const baseFontSize = options?.baseFontSize ?? 10;
    const minFontSize = options?.minFontSize ?? 8;
    const targetPadding = options?.targetPadding ?? 0;
    const lineHeight = options?.lineHeight ?? 0.9;
    const forceSingleTable = options?.forceSingleTable ?? true;

    // Prepare headers/fields arrays
    const headers = pdfColumns.map(c => c.header.toUpperCase());
    const fields = pdfColumns.map(c => c.field);

    // Fit computation to keep all columns together
    const margins: [number, number, number, number] = [40, 60, 40, 40];
    const { fontSize: fittedFontSize, pageSize: fittedPageSize } =
      forceSingleTable
        ? computeFitSettings(headers, fields, data, pageSize, orientation, margins, baseFontSize, minFontSize, targetPadding)
        : { fontSize: baseFontSize, pageSize };

    // Header row with minimal padding
    const headerRow = headers.map(text => ({
      text,
      bold: true,
      color: '#0D47A1',
      fillColor: '#E3F2FD',
      margin: [0, targetPadding, 0, targetPadding],
      noWrap: true
    }));

    const isDayColumn = (key: string) => /^\d{4}_\d{2}_\d{2}$/.test(key);

    // Build body rows
    const body = [
      headerRow,
      ...data.map(row =>
        fields.map(field => {
          const cfg = colConfigs.find(c => c.jsonKey === field)!;

          let txt = formatCellByType(cfg, row[field], 'en-IN');

          // Sanitization rules
          txt = txt.replace(/\r?\n|\r/g, ' ').replace(/\s+/g, ' ').trim();
          if (options.fieldsToTrim?.has(field) && txt.length > 40) {
            txt = txt.slice(0, 40);
          }

          const cell: any = {
            text: txt,
            alignment: cfg.dataType === 'number' ? 'right' : 'left',
            margin: [0, targetPadding, 0, targetPadding],
            lineHeight            
          };

          // Color by value for dynamic columns only
          if (isDayColumn(field)) {
            const v = String(row[field] ?? '').toUpperCase();

            if (v.includes('COMPLETED') || v.includes('SUCCESS')) {
              cell.fillColor = '#c6f6c6';
              cell.color = '#000000';
              cell.bold = true;
            } else if (v.includes('LOAD DID NOT RUN') || v.includes('FAILED') || v.includes('ERROR') || v.includes('UNHEALTHY') || v.includes('INTERRUPTED')) {
              cell.fillColor = '#f8c6c6';
              cell.color = '#B71C1C';
              cell.bold = true;
            } else if (v.includes('PROGRESS') || v.includes('HEALTHY') || v.includes('BATCH')) {
              cell.fillColor = '#fff3cd';
              cell.color = '#856404';
            }
          }

          return cell;
        })
      )
    ];

    // All columns together: single widths array (auto)
    const widths = fields.map(() => 'auto');

    // Doc definition
    const now = new Date();
    const generatedAt = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

    const docDefinition: any = {
      info: { title, subject: `${title} (${generatedAt})` },
      pageSize: fittedPageSize,
      pageOrientation: orientation,
      pageMargins: margins,
      defaultStyle: { fontSize: fittedFontSize, lineHeight },

      watermark: { text: watermarkText, color: '#777', opacity: 0.12, angle: 35, bold: true },

      header: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: title, style: 'headerLeft', color: '#1976D2' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'right', style: 'headerRight', color: '#555' }
        ],
        margin: [40, 20, 40, 0]
      }),

      footer: () => ({
        columns: [
          { text: `Generated: ${generatedAt}`, alignment: 'left', color: '#444' },
          { text: new Date().toLocaleTimeString(), alignment: 'right', color: '#444' }
        ],
        margin: [40, 10, 40, 0],
        fontSize: 9
      }),

      content: [
        { text: title, style: 'title', color: '#0D47A1', margin: [0, 0, 0, 8] },
        {
          table: { headerRows: 1, widths, body },
          layout: {
            paddingLeft: () => targetPadding,
            paddingRight: () => targetPadding,
            paddingTop: () => targetPadding,
            paddingBottom: () => targetPadding,
            hLineWidth: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? 0.8 : 0.4,
            vLineWidth: () => 0.4,
            hLineColor: (i: number, node: any) => (i === 0 || i === node.table.body.length) ? '#B0BEC5' : '#CFD8DC',
            vLineColor: () => '#CFD8DC'
          }
        }
      ],

      styles: {
        title: { fontSize: Math.max(12, fittedFontSize + 2), bold: true },
        headerLeft: { fontSize: 9 },
        headerRight: { fontSize: 9 }
      }
    };

    pdfMake.createPdf(docDefinition).download(`${options.exportFileName}`);
  }

  generateFilename(name: string, format: string) {
    const today = moment();
    name = name.replace(' ', '_');
    // corner case for assign and configure release
    name =
      name.includes('Assign') || name.includes('Configure')
        ? name + '_Release'
        : name;
    const dateString = today.format('YYYY-MM-DD-HH-mm-ss');
    // Concatenate the date with the name
    return `${name}_${dateString}.${format}`;
  }

  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
  }


  isErrorLog(element) {
    // Use the provided isErrorLog function if available
    if (typeof this.customIsErrorLog === 'function') {
      return this.customIsErrorLog(element);
    }

    // Otherwise use the default implementation
    if (element.pmsStatusCode) {
      return ACKNOWLEDGEABLE_ERROR_STATUS_CODES.includes(element.pmsStatusCode)
        ? true
        : false;
    }
    return false;
  }

  addAllOption() {
    if (!(this.gridConfig?.showAllPagination === false)) {
      const lastOption = document.querySelector(
        '.mat-mdc-select-panel-above .mat-mdc-option:last-child > span'
      );
      lastOption ? (lastOption.innerHTML = 'All') : null;
    }
  }

  getPageSize() {
    this.gridConfig.showAllPagination == false
      ? this.pageSizeOptions.pop()
      : null;
    return this.pageSizeOptions;
  }

  getSelectedOptionName(filterConfig: FilterConfig): string {
    const selectedOption = filterConfig.options?.data.find(
      (option: any) => option.id === this.filters[filterConfig.jsonKey]
    );
    return selectedOption ? selectedOption.name : '';
  }

  isAllOptionsSelected(filterConfig: FilterConfig): boolean {
    const selectedFilters = this.filters[filterConfig.jsonKey] || [];
    const hasAllOption = selectedFilters.some((filter: any) => filter.id === 'All');

    if (hasAllOption) {
      return true;
    }

    const allNonAllOptions = (filterConfig.options?.data || []).filter(option => option.id !== 'All');
    const selectedNonAllOptions = selectedFilters.filter((filter: any) => filter.id !== 'All');

    return allNonAllOptions.length > 0 && selectedNonAllOptions.length === allNonAllOptions.length;
  }

  applyFilter(filterValue: string) {
    const searchQuery = filterValue.trim().toLowerCase();
    this.searchSubject.next(searchQuery);
  }

  applyFilters(filterConfig): void {
    localStorage.setItem('choosenFilters', JSON.stringify(this.filters));

    this.appliedFilter = true;
    if (this.filters[filterConfig.jsonKey] === 'custom') {
      const fromDate = new Date(this.customDateRange.from);
      const toDate = new Date(this.customDateRange.to);

      this.filters[filterConfig.jsonKey] = {
        startDate: fromDate.toISOString(),
        endDate: toDate.toISOString(),
      };
    } else if (typeof this.filters[filterConfig.jsonKey] === 'object') {
      this.customDateRange.from = this.filters[filterConfig.jsonKey].startDate;
      this.customDateRange.to = this.filters[filterConfig.jsonKey].endDate;
    }
    this.pageNumber = 1;
    this.fetchData();
    this.closeFilterDropdowns();
    this.getDateFilterLabel();
  }

  clearFilters(filterConfig: FilterConfig): void {
    this.filters[filterConfig.jsonKey] = [];
    this.closeFilterDropdowns();
  }

  cancelFilters(filterConfig: FilterConfig): void {
    this.filters = {
      pmsIds: [],
      sortOrder: 'asc',
      isLive: null
    };
    this.isLiveFilterActive = false;

    this.resetAll = true;
    localStorage.removeItem('choosenFilters');
    this.appliedFilter = false;
    this.fetchData();
    this.closeFilterDropdowns();
  }

  closeFilterDropdowns(): void {
    Object.keys(this.isFilterDropdownOpen).forEach((key) => {
      this.isFilterDropdownOpen[key] = false;
    });
  }

  handleError(error: any): void {
    console.error('Error fetching data:', error);
    this.showErrorCard = true;
    this.isLoading = false;

    if (error.error && error.error.errorMessage) {
      this.errorTitle = error.error.errorMessage;
      this.errorMessage =
        error.error.detailedErrorMessage ||
        'No detailed error message provided.';
    } else if (error.status === 408) {
      this.errorTitle = 'Request Timeout';
      this.errorMessage =
        'The server is taking too long to respond. Please check your internet connection and try again.';
    } else if (error.status === 403) {
      this.errorTitle = 'Forbidden';
      this.errorMessage =
        "You don't have permission to access this information. If you believe this is a mistake, please contact support.";
    } else if (error.status === 404) {
      this.errorTitle = 'Not Found';
      this.errorMessage =
        'The requested information could not be found. It may have been moved or deleted.';
    } else if (error.status >= 500) {
      this.errorTitle = 'Internal Server Error';
      this.errorMessage =
        'There was a problem with the server. Please try again later or contact support if the problem persists.';
    } else {
      this.errorTitle = error.error?.errorMessage || 'Unexpected Error';
      this.errorMessage = error.error?.detailedErrorMessage ||
        'An unexpected error occurred. Please try again or contact support if the problem continues.';
    }

    this.changeDetectorRef.detectChanges();
  }

  onReloadAfterError() {
    this.closeErrorCard();
    this.fetchData();
  }

  closeErrorCard() {
    this.showErrorCard = false;
    this.changeDetectorRef.detectChanges();
  }

  calculateStatusCounts(): void {
    this.statusLegend.forEach(legend => legend.count = 0);
    this.totalCount = 0;

    this.originalData.forEach((row: any) => {
      if (row.isDisabled) {
        this.statusLegend[5].count++; // Disabled count
        return;
      }

      if (!row.isInstalled) {
        this.statusLegend[4].count++; // Not Installed count
        return;
      }

      this.gridConfig.columns.forEach(column => {
        if (column.jsonKey !== 'practiceCode' && column.jsonKey !== 'pmsName' && column.jsonKey !== 'isLive') {
          const status = row[column.jsonKey];
          const colors = this.getStatusColor(status, row.isDisabled, row.isInstalled);

          if (colors.backgroundColor === '#c6f6c6') {
            this.statusLegend[0].count++;
          } else if (colors.backgroundColor === '#fff3cd') {
            this.statusLegend[1].count++;
          } else if (colors.backgroundColor === '#f8c6c6') {
            // Check if it's a load didn't run case for active practice
            if (!status || status === '' || status === null || status === undefined ||
              status.toString().trim() === '' || status.toString().trim() === '-' ||
              status.toString().trim() === 'LOAD DID NOT RUN') {
              this.statusLegend[3].count++; // Load Didn't Run
            } else {
              // Only count actual error/failed statuses
              const statusLower = status.toLowerCase();
              if (statusLower.includes('failed') || statusLower.includes('error') ||
                statusLower.includes('unhealthy') || statusLower.includes('interrupted')) {
                this.statusLegend[2].count++; // Error/Failed
              }
            }
          }
          this.totalCount++;
        }
      });
    });

    // Add disabled and not installed to total count
    this.totalCount += this.statusLegend[4].count + this.statusLegend[5].count;
  }

  onColumnHeaderClick(columnKey: string): void {
    if (columnKey === 'practiceCode' || columnKey === 'pmsName' || columnKey === 'isLive') return;

    this.selectedColumn = this.gridConfig.columns.find(col => col.jsonKey === columnKey)?.label || columnKey;
    this.selectedColumnKey = columnKey;
    this.statusLegend.forEach(legend => legend.count = 0);
    this.totalCount = 0;

    this.originalData.forEach((row: any) => {
      if (row.isDisabled) {
        this.statusLegend[5].count++;
        return;
      }

      if (!row.isInstalled) {
        this.statusLegend[4].count++;
        return;
      }

      const status = row[columnKey];
      const colors = this.getStatusColor(status, row.isDisabled, row.isInstalled);

      if (colors.backgroundColor === '#c6f6c6') {
        this.statusLegend[0].count++;
      } else if (colors.backgroundColor === '#fff3cd') {
        this.statusLegend[1].count++;
      } else if (colors.backgroundColor === '#f8c6c6') {
        // Check if it's a load didn't run case for active practice
        if (!status || status === '' || status === null || status === undefined ||
          status.toString().trim() === '' || status.toString().trim() === '-' ||
          status.toString().trim() === 'LOAD DID NOT RUN') {
          this.statusLegend[3].count++; // Load Didn't Run
        } else {
          // Only count actual error/failed statuses
          const statusLower = status.toLowerCase();
          if (statusLower.includes('failed') || statusLower.includes('error') ||
            statusLower.includes('unhealthy') || statusLower.includes('interrupted')) {
            this.statusLegend[2].count++; // Error/Failed
          }
        }
      }
      this.totalCount++;
    });

    // Add disabled and not installed to total count
    this.totalCount += this.statusLegend[4].count + this.statusLegend[5].count;
  }

  resetLegendToTotal(): void {
    this.selectedColumn = '';
    this.selectedColumnKey = '';
    this.isFiltered = false;
    this.dataSource.data = [...this.originalData];
    this.calculateStatusCounts();
    this.changeDetectorRef.detectChanges();
  }

  onLegendClick(legendItem: any): void {
    // Don't filter if the legend item has no records
    if (legendItem.count === 0) {
      return;
    }

    this.isFiltered = true;

    const filteredData = this.originalData.filter((row: any) => {
      if (legendItem.label === 'Disabled') {
        return row.isDisabled === true;
      }

      if (legendItem.label === 'Not Installed') {
        return !row.isInstalled && !row.isDisabled;
      }

      if (legendItem.label === 'Load Didn\'t Run') {
        return !row.isDisabled && row.isInstalled && this.gridConfig.columns.some(column => {
          if (column.jsonKey === 'practiceCode' || column.jsonKey === 'pmsName' || column.jsonKey === 'isLive') return false;
          const status = row[column.jsonKey];
          return (!status || status === '' || status === null || status === undefined ||
            status.toString().trim() === '' || status.toString().trim() === '-' ||
            status.toString().trim() === 'LOAD DID NOT RUN');
        });
      }

      if (legendItem.label === 'Error/Failed') {
        return !row.isDisabled && row.isInstalled && this.gridConfig.columns.some(column => {
          if (column.jsonKey === 'practiceCode' || column.jsonKey === 'pmsName' || column.jsonKey === 'isLive') return false;
          const status = row[column.jsonKey];
          if (!status) return false;
          const statusLower = status.toLowerCase();
          return (statusLower.includes('failed') || statusLower.includes('error') ||
            statusLower.includes('unhealthy') || statusLower.includes('interrupted'));
        });
      }

      // For Completed and In Progress, use color-based filtering
      if (legendItem.label === 'Completed' || legendItem.label === 'In Progress') {
        if (row.isDisabled || !row.isInstalled) return false;

        // If a specific column is selected, filter only by that column
        if (this.selectedColumnKey) {
          const status = row[this.selectedColumnKey];
          const colors = this.getStatusColor(status, row.isDisabled, row.isInstalled);
          return colors.backgroundColor === legendItem.color;
        }

        // Otherwise, filter by any column matching the status
        return this.gridConfig.columns.some(column => {
          if (column.jsonKey === 'practiceCode' || column.jsonKey === 'pmsName' || column.jsonKey === 'isLive') return false;

          const status = row[column.jsonKey];
          const colors = this.getStatusColor(status, row.isDisabled, row.isInstalled);

          return colors.backgroundColor === legendItem.color;
        });
      }

      return false;
    });

    this.dataSource.data = filteredData;
    this.changeDetectorRef.detectChanges();
  }

  //******************************// 
  // Column Prefrences //
  //******************************// 
  initializeVisibleColumns() {
    const hiddenColumns = this.columnPreference.HiddenColumns;
    this.gridConfig.columns.forEach(col => {
      if (!hiddenColumns.has(col.jsonKey)) {
        this.visibleColumns.add(col.jsonKey);
      }
    });
  }

  getPreferenceKeyName(): ColumnPreference {
    switch (this.uniqueTableName) {
      case 'PMSMaster':
        return {
          PreferenceKeyName: 'pms_master',
          HiddenColumns: new Set([])
        };
      case 'PracticeMaster':
        return {
          PreferenceKeyName: 'practice_master',
          HiddenColumns: new Set([])
        };
      case 'PMSObject':
        return {
          PreferenceKeyName: 'pms_object',
          HiddenColumns: new Set([])
        };
      case 'PHIMaster':
        return {
          PreferenceKeyName: 'phi_master',
          HiddenColumns: new Set([])
        };
      case 'LoadSchedule':
        return {
          PreferenceKeyName: 'load_schedule',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadConfiguration':
        return {
          PreferenceKeyName: 'practice_load_configuration',
          HiddenColumns: new Set([])
        };

      case 'PracticeInstallationLog':
        return {
          PreferenceKeyName: 'practice_installation_log_report',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentHealthStatusReport':
        return {
          PreferenceKeyName: 'pms_agent_health_status_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeErrorLog':
        return {
          PreferenceKeyName: 'practice_error_log_report',
          HiddenColumns: new Set([])
        };
      case 'PracticeLoadDetail':
        return {
          PreferenceKeyName: 'practice_load_detail_report',
          HiddenColumns: new Set([])
        };
      case 'UserLogs':
        return {
          PreferenceKeyName: 'user_logs_report',
          HiddenColumns: new Set([])
        };
      case 'AuditReport':
        return {
          PreferenceKeyName: 'audit_report',
          HiddenColumns: new Set([])
        };
      case 'LastSevenDaysLoadReport':
        return {
          PreferenceKeyName: 'last_seven_days_load_report',
          HiddenColumns: new Set([])
        };

      case 'PMSAgentRelease':
        return {
          PreferenceKeyName: 'pms_agent_releasec',
          HiddenColumns: new Set([])
        };
      case 'PMSAgentReleaseMapping':
        return {
          PreferenceKeyName: 'pms_agent_release_mapping',
          HiddenColumns: new Set([])
        };

      case 'UserRoleManagement':
        return {
          PreferenceKeyName: 'user_role_management',
          HiddenColumns: new Set([])
        };

      default:
        return {
          PreferenceKeyName: '',
          HiddenColumns: new Set([])
        };
    }
  }

  toggleColumnSelector(event: MouseEvent) {
    event.stopPropagation();
    this.isColumnSelectorOpen = !this.isColumnSelectorOpen;
  }

  toggleColumnVisibility(jsonKey: string) {
    if (this.visibleColumns.has(jsonKey)) {
      this.visibleColumns.delete(jsonKey);
    } else {
      this.visibleColumns.add(jsonKey);
    }
  }

  isColumnVisible(jsonKey: string): boolean {
    return this.visibleColumns.has(jsonKey);
  }

  closeAllDropdowns(key: string) {
    this.isDropdownOpen = false;
    this.isColumnSelectorOpen = false;
    this.changeDetectorRef.markForCheck();
    this.isFilterDropdownOpen[key] = false;
    this.changeDetectorRef.detectChanges();
  }

  onSavePreferences(event: MouseEvent) {
    event.stopPropagation();
    this.saveColumnPreferences();
  }

  async loadColumnPreferences(preferenceKeyName: string): Promise<void> {
    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
      if (!this.userId) {
        const userClaims = await this.oktaAuth.getUser();
        this.userId = userClaims?.sub || null;
        localStorage.setItem('externalUserId', this.userId || '');
      }
    }

    (await this.columnPrefService.getColumnPreferences(preferenceKeyName))
      .subscribe(prefs => {
        if (prefs !== null) {
          const preferences = JSON.parse(prefs.preferenceValue);
          this.visibleColumns = new Set(preferences.columns);
        } else {

        }
      });
  }

  saveColumnPreferences(): void {
    this.isSavingPreferences = true;

    if (!this.userId) {
      this.userId = localStorage.getItem('externalUserId');
    }

    const body = {
      externalUserId: this.userId,
      preferenceKey: this.columnPreference.PreferenceKeyName,
      preferenceValue: JSON.stringify({ columns: Array.from(this.visibleColumns) })
    };

    this.columnPrefService
      .saveColumnPreferences(this.columnPreference.PreferenceKeyName, body)
      .pipe(
        finalize(() => {
          this.isSavingPreferences = false;
          this.changeDetectorRef.markForCheck(); // Important for OnPush
        })
      )
      .subscribe({
        next: (ok) => {
          // Ensure UI updates are within Angular zone
          this.changeDetectorRef.markForCheck();
          this.isSavingPreferences = false;
          this.toastComponent?.showToast({
            severity: 'success',
            title: 'Success',
            message: 'Column preferences saved successfully',
            duration: 3000
          });
        },
        error: (err) => {

          console.error('Failed to save column preferences', err);
          // Optionally show error toast
          this.toastComponent?.showToast({
            severity: 'error',
            title: 'Error',
            message: 'Failed to save column preferences',
            duration: 3000
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }
}
